import React, { useState } from 'react';
import { AcvtGenHeader } from '../generic-header';
import style from './style.module.scss';
import ArrowIcon from '../../../icons/left-arrow.svg?react';
import { AcvtRedSticker } from '../red-sticker';
import PromotionsIcon from '../../../icons/Icon_Promotions_1X.svg?react';
import parser from 'html-react-parser';
import { AcvtPromotion, AcvtSupportedLanguages } from '../../../types/acvt';
import moment, { Moment } from 'moment';
import * as Colors from '../../../styles/colors';
import { useT, usePreview } from '../../preview';
import { formatDate } from '~utils/format';
moment.locale('en');

type PromoInfo = {
    /**
     * Kept for backward compatibility — the component now reads its
     * strings from `useT()` and the active language from `usePreview()`.
     */
    language?: AcvtSupportedLanguages;
    promoObject?: AcvtPromotion;
    opened?: React.Dispatch<
        React.SetStateAction<{
            homePromoCard: boolean;
            homeGameCard: boolean;
            promoCard: boolean;
            gameCard: boolean;
        }>
    >;
    onEditMode?: boolean;
};

export const AcvtPromotionPage = ({
    promoObject,
    opened,
    onEditMode = false,
}: PromoInfo) => {
    const t = useT();
    const { language } = usePreview();
    const [isIconHovering, setIsIconHovering] = useState(false);
    const hasPromoOfferTitle = (() => {
        const title = promoObject?.promotionalOffer?.title;
        if (typeof title !== 'string') {
            return false;
        }
        const normalized = title.trim();
        return normalized.length > 0 && normalized !== 'undefined';
    })();

    const handleMouseEnter = () => {
        setIsIconHovering(true);
    };

    const handleMouseLeave = () => {
        setIsIconHovering(false);
    };

    const convertToAMPM = (hour: number) => {
        if (hour <= 12) {
            return `${hour}AM`;
        } else {
            return `${hour - 12}PM`;
        }
    };

    const now = moment();
    const currentHour = now.hours();
    const activationStart = promoObject?.activatableDate
        ? moment(promoObject.activatableDate)
        : null;
    const activationEnd = promoObject?.inactivatableDate
        ? moment(promoObject.inactivatableDate)
        : null;
    const startPeriod = promoObject?.startPeriod ?? null;
    const endPeriod = promoObject?.endPeriod ?? null;

    const noActivationWindow = !activationStart && !activationEnd;
    const withinActivationWindow =
        activationStart && activationEnd
            ? now.isBetween(activationStart, activationEnd, undefined, '[]')
            : false;
    const beforeActivation = activationStart
        ? now.isBefore(activationStart)
        : false;

    const meetsStartPeriod = startPeriod == null || currentHour >= startPeriod;
    const meetsEndPeriod = endPeriod == null || currentHour <= endPeriod;
    const hasOvernightPeriod =
        typeof startPeriod === 'number' &&
        typeof endPeriod === 'number' &&
        endPeriod < startPeriod;
    const matchesOvernightPeriod =
        hasOvernightPeriod &&
        ((startPeriod != null && currentHour >= startPeriod) ||
            (endPeriod != null && currentHour <= endPeriod));

    const withinDailyPeriod =
        startPeriod == null && endPeriod == null
            ? true
            : (meetsStartPeriod && meetsEndPeriod) || matchesOvernightPeriod;

    const couponActive =
        !promoObject || noActivationWindow
            ? true
            : withinActivationWindow && withinDailyPeriod;

    const promotionContentStyle =
        !promoObject || onEditMode || noActivationWindow || couponActive
            ? {}
            : { opacity: 0.5 };

    const bannerFilter =
        !promoObject ||
        onEditMode ||
        withinActivationWindow ||
        beforeActivation ||
        noActivationWindow
            ? ''
            : 'grayscale(1)';

    const showCouponButton =
        !!promoObject &&
        (withinActivationWindow || beforeActivation || noActivationWindow);

    const formattedTimeRange =
        typeof startPeriod === 'number' && typeof endPeriod === 'number'
            ? language === 'fr'
                ? `${startPeriod}h${t.promotion.timeRangeJoin}${endPeriod}h`
                : `${convertToAMPM(startPeriod)}${
                      t.promotion.timeRangeJoin
                  }${convertToAMPM(endPeriod)}`
            : t.promotion.upcoming;

    const couponLabel = (() => {
        if (!promoObject) {
            return '';
        }
        if (couponActive) {
            return t.promotion.use;
        }
        if (withinActivationWindow) {
            return formattedTimeRange;
        }
        if (beforeActivation) {
            return t.promotion.upcoming;
        }
        return t.promotion.expired;
    })();

    const couponButtonDisabled = !promoObject || !couponActive;

    const activationDatesLabel = (() => {
        if (!promoObject) {
            return '---';
        }

        const start =
            activationStart && activationEnd
                ? formatDate(activationStart, language)
                : formatDate(promoObject?.startDate, language);
        const end =
            activationStart && activationEnd
                ? formatDate(activationEnd, language)
                : formatDate(promoObject?.endDate, language);

        return `${t.promotion.validBetweenStart}${start}${t.promotion.validBetweenJoin}${end}`;
    })();

    return (
        <div className={style.page_content}>
            <AcvtGenHeader>
                <ArrowIcon
                    fill={isIconHovering ? 'black' : 'grey'}
                    style={{ cursor: 'pointer' }}
                    onMouseEnter={handleMouseEnter}
                    onMouseLeave={handleMouseLeave}
                    className={style.back_arrow}
                    onClick={() =>
                        opened &&
                        opened((prevState) => {
                            return {
                                ...prevState,
                                promoCard: false,
                                homePromoCard: false,
                            };
                        })
                    }
                />
                {t.promotion.title}
            </AcvtGenHeader>
            <div
                className={style.promotion_content}
                style={promotionContentStyle}
            >
                <div
                    className={style.promo_banner}
                    style={{
                        backgroundColor:
                            promoObject?.backgroundColor ||
                            Colors.UNKNOWN_GRAY_f7f7f6,
                        ...(!promoObject?.backgroundColor && {
                            backgroundImage: `url("${promoObject?.background}")`,
                            backgroundRepeat: 'no-repeat',
                            backgroundSize: 'cover',
                        }),
                        filter: bannerFilter,
                    }}
                >
                    <div className={style.red_sticker}>
                        {promoObject?.type === 'coupon' &&
                            hasPromoOfferTitle && (
                                <AcvtRedSticker
                                    offer={promoObject?.promotionalOffer?.title}
                                />
                            )}
                    </div>
                    <img alt="" src={promoObject?.thumbnail ?? ''} />
                </div>
                <div>
                    <div className={style.text_grid}>
                        <div className={style.category}>
                            {promoObject?.category.title}
                        </div>
                        <div className={style.title}>{promoObject?.title}</div>
                        <div className={style.dates}>
                            {activationDatesLabel}
                        </div>
                        {promoObject?.type === 'coupon' && hasPromoOfferTitle && (
                            <div className={style.coupon_button_container}>
                                {showCouponButton ? (
                                    <button
                                        className={style.coupon_button}
                                        disabled={couponButtonDisabled}
                                    >
                                        <span className={style.coupon_icon}>
                                            <PromotionsIcon />
                                        </span>
                                        {couponLabel}
                                    </button>
                                ) : (
                                    <div
                                        style={{
                                            color: 'red',
                                        }}
                                    >
                                        {t.promotion.expired}
                                    </div>
                                )}
                            </div>
                        )}
                        <div className={style.description}>
                            {parser(promoObject?.description ?? '')}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};
